"use client";

import { ArrowLeft, MapPin, Grape, BookOpen } from 'lucide-react';
import Link from 'next/link';
import { ImageWithFallback } from '@/figma/components/figma/ImageWithFallback';
import type { EducationPageContent } from '@/lib/page-content-types';

const regions = [
  {
    name: 'Galilee',
    location: 'Northern Israel',
    description: 'Cool climate region with elevation up to 1,200m. Known for elegant Cabernet Sauvignon and Syrah.',
    image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800'
  },
  {
    name: 'Judean Hills',
    location: 'Central Israel',
    description: 'High altitude vineyards around Jerusalem. Limestone-rich soils produce structured, age-worthy wines.',
    image: 'https://images.unsplash.com/photo-1474557157379-8aa74a6ef541?w=800'
  },
  {
    name: 'Golan Heights',
    location: 'Northeast Israel',
    description: 'Volcanic soils and cool nights. Israel\'s premium region for Bordeaux varieties.',
    image: 'https://images.unsplash.com/photo-1523825391079-0092e2f2baaf?w=800'
  }
];

const grapes = [
  {
    name: 'Cabernet Sauvignon',
    type: 'Red',
    character: 'Full-bodied, structured, age-worthy',
    regions: 'Galilee, Golan Heights'
  },
  {
    name: 'Syrah',
    type: 'Red',
    character: 'Spicy, elegant, mineral-driven',
    regions: 'Galilee, Judean Hills'
  },
  {
    name: 'Carignan',
    type: 'Red',
    character: 'Old-vine character, rustic, earthy',
    regions: 'Judean Hills, Negev'
  },
  {
    name: 'Chardonnay',
    type: 'White',
    character: 'Elegant, mineral, Mediterranean fruit',
    regions: 'Golan Heights, Galilee'
  },
  {
    name: 'Sauvignon Blanc',
    type: 'White',
    character: 'Fresh, aromatic, citrus-driven',
    regions: 'Golan Heights'
  },
  {
    name: 'Mediterranean Varieties',
    type: 'White',
    character: 'Grenache Blanc, Roussanne, Viognier',
    regions: 'All regions'
  }
];

const guides = [
  {
    title: 'Understanding Israeli Wine Terroir',
    description: 'How altitude, soil, and Mediterranean climate shape Israeli wine character.',
    readTime: '8 min'
  },
  {
    title: 'Old Vines, New Expression',
    description: 'The revival of ancient vineyard sites and forgotten grape varieties.',
    readTime: '12 min'
  },
  {
    title: 'Kosher Wine Production',
    description: 'The process, philosophy, and quality of modern kosher winemaking.',
    readTime: '6 min'
  },
  {
    title: 'Collector\'s Guide to Israeli Wine',
    description: 'Which wines to age, optimal cellaring conditions, and vintage guidance.',
    readTime: '10 min'
  }
];

export function EducationPage({ content }: { content?: EducationPageContent | null }) {
  const regionsData = content?.regions?.length ? content.regions : regions;
  const grapesData = content?.grapes?.length ? content.grapes : grapes;
  const guidesData = content?.guides?.length ? content.guides : guides;

  return (
    <div className="min-h-screen" style={{ fontFamily: 'var(--font-body)' }}>
      {/* Back navigation */}
      <div style={{ backgroundColor: 'var(--linen)', borderBottom: '1px solid var(--stone)' }}>
        <div className="mx-auto max-w-screen-2xl px-8 lg:px-16 py-8">
          <Link 
            href="/"
            className="inline-flex items-center gap-2 transition-opacity duration-200 hover:opacity-50"
            style={{ 
              fontFamily: 'var(--font-body)',
              fontSize: 'var(--text-sm)',
              color: 'var(--charcoal)',
              fontWeight: 400
            }}
          >
            <ArrowLeft size={16} />
            Back to Home
          </Link>
        </div>
      </div>

      {/* Hero */}
      <section style={{ backgroundColor: 'var(--paper)' }}>
        <div className="mx-auto max-w-screen-2xl px-8 lg:px-16">
          <div className="py-24 lg:py-32">
            <div className="max-w-4xl">
              <div className="mb-8">
                <span 
                  style={{ 
                    fontFamily: 'var(--font-display)',
                    fontSize: 'var(--text-sm)',
                    fontWeight: 300,
                    color: 'var(--slate)',
                    letterSpacing: '0.05em'
                  }}
                >
                  01
                </span>
              </div>
              
              <h1 
                className="mb-8"
                style={{ 
                  fontFamily: 'var(--font-display)',
                  fontSize: 'var(--text-5xl)',
                  fontWeight: 300,
                  lineHeight: 'var(--line-height-tight)',
                  color: 'var(--charcoal)',
                  letterSpacing: '-0.02em'
                }}
              >
                Education
              </h1>
              
              <p 
                style={{ 
                  fontFamily: 'var(--font-body)',
                  fontSize: 'var(--text-xl)',
                  lineHeight: 'var(--line-height-body)',
                  color: 'var(--slate)',
                  fontWeight: 300,
                  maxWidth: '720px'
                }}
              >
                From ancient terroirs to modern winemaking, explore the comprehensive guide to Israeli wine regions, grape varieties, and the stories that make this one of the world&apos;s most exciting wine countries.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Regions Section */}
      <section style={{ backgroundColor: 'var(--linen)' }}>
        <div className="mx-auto max-w-screen-2xl px-8 lg:px-16">
          <div className="py-24 lg:py-32">
            <div className="mb-16">
              <div className="flex items-center gap-3 mb-6">
                <MapPin size={24} style={{ color: 'var(--oxblood)' }} />
                <h2 
                  style={{ 
                    fontFamily: 'var(--font-display)',
                    fontSize: 'var(--text-3xl)',
                    fontWeight: 300,
                    lineHeight: 'var(--line-height-tight)',
                    color: 'var(--charcoal)',
                    letterSpacing: '-0.02em'
                  }}
                >
                  Wine Regions
                </h2>
              </div>
              <p 
                style={{ 
                  fontFamily: 'var(--font-body)',
                  fontSize: 'var(--text-base)',
                  lineHeight: 'var(--line-height-body)',
                  color: 'var(--slate)',
                  fontWeight: 300,
                  maxWidth: '640px'
                }}
              >
                Israel&apos;s diverse topography creates distinct wine regions, each with unique terroir characteristics shaped by elevation, climate, and soil composition.
              </p>
            </div>

            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {regionsData.map((region) => (
                <Link 
                  key={region.name}
                  href="/education/regions"
                  style={{ backgroundColor: 'var(--paper)' }}
                  className="group cursor-pointer transition-all duration-300 hover:shadow-lg"
                >
                  <div className="aspect-[4/3] overflow-hidden" style={{ backgroundColor: 'var(--stone)' }}>
                    <ImageWithFallback
                      src={region.image}
                      alt={region.name}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-8">
                    <h3 
                      className="mb-2"
                      style={{ 
                        fontFamily: 'var(--font-display)',
                        fontSize: 'var(--text-xl)',
                        fontWeight: 300,
                        color: 'var(--charcoal)'
                      }}
                    >
                      {region.name}
                    </h3>
                    <p 
                      className="mb-4"
                      style={{ 
                        fontFamily: 'var(--font-body)',
                        fontSize: 'var(--text-sm)',
                        color: 'var(--oxblood)',
                        fontWeight: 400
                      }}
                    >
                      {region.location}
                    </p>
                    <p 
                      style={{ 
                        fontFamily: 'var(--font-body)',
                        fontSize: 'var(--text-sm)',
                        lineHeight: 'var(--line-height-body)',
                        color: 'var(--slate)',
                        fontWeight: 300
                      }}
                    >
                      {region.description}
                    </p>
                  </div>
                </Link>
              ))}
            </div>

            <div className="mt-12 text-center">
              <Link 
                href="/education/regions"
                className="inline-flex items-center gap-2 px-8 py-4 transition-opacity duration-200 hover:opacity-80"
                style={{ 
                  backgroundColor: 'var(--charcoal)',
                  color: 'var(--paper)',
                  fontFamily: 'var(--font-body)',
                  fontSize: 'var(--text-base)',
                  fontWeight: 400
                }}
              >
                Explore All Regions
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Grapes Section */}
      <section style={{ backgroundColor: 'var(--paper)' }}>
        <div className="mx-auto max-w-screen-2xl px-8 lg:px-16">
          <div className="py-24 lg:py-32">
            <div className="mb-16">
              <div className="flex items-center gap-3 mb-6">
                <Grape size={24} style={{ color: 'var(--oxblood)' }} />
                <h2 
                  style={{ 
                    fontFamily: 'var(--font-display)',
                    fontSize: 'var(--text-3xl)',
                    fontWeight: 300,
                    lineHeight: 'var(--line-height-tight)',
                    color: 'var(--charcoal)',
                    letterSpacing: '-0.02em'
                  }}
                >
                  Grape Varieties
                </h2>
              </div>
              <p 
                style={{ 
                  fontFamily: 'var(--font-body)',
                  fontSize: 'var(--text-base)',
                  lineHeight: 'var(--line-height-body)',
                  color: 'var(--slate)',
                  fontWeight: 300,
                  maxWidth: '640px'
                }}
              >
                A blend of international varieties and Mediterranean heritage grapes that thrive in Israel&apos;s unique climate.
              </p>
            </div>

            <div className="grid gap-6 md:grid-cols-2">
              {grapesData.map((grape) => (
                <div 
                  key={grape.name}
                  className="p-8 border transition-colors duration-200 hover:bg-[var(--linen)]"
                  style={{ borderColor: 'var(--stone)' }}
                >
                  <div className="flex justify-between items-start mb-4">
                    <h3 
                      style={{ 
                        fontFamily: 'var(--font-display)',
                        fontSize: 'var(--text-xl)',
                        fontWeight: 300,
                        color: 'var(--charcoal)'
                      }}
                    >
                      {grape.name}
                    </h3>
                    <span 
                      className="px-3 py-1"
                      style={{ 
                        fontFamily: 'var(--font-body)',
                        fontSize: 'var(--text-xs)',
                        backgroundColor: grape.type === 'Red' ? 'var(--oxblood)' : 'var(--olive-mist)',
                        color: grape.type === 'Red' ? 'var(--paper)' : 'var(--charcoal)',
                        fontWeight: 400,
                        letterSpacing: '0.05em'
                      }}
                    >
                      {grape.type.toUpperCase()}
                    </span>
                  </div>
                  <p 
                    className="mb-3"
                    style={{ 
                      fontFamily: 'var(--font-body)',
                      fontSize: 'var(--text-sm)',
                      lineHeight: 'var(--line-height-body)',
                      color: 'var(--slate)',
                      fontWeight: 300
                    }}
                  >
                    {grape.character}
                  </p>
                  <p 
                    style={{ 
                      fontFamily: 'var(--font-body)',
                      fontSize: 'var(--text-xs)',
                      color: 'var(--slate)',
                      fontWeight: 400
                    }}
                  >
                    Primary Regions: {grape.regions}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-12 text-center">
              <Link 
                href="/education/grapes"
                className="inline-flex items-center gap-2 px-8 py-4 transition-opacity duration-200 hover:opacity-80"
                style={{ 
                  backgroundColor: 'var(--charcoal)',
                  color: 'var(--paper)',
                  fontFamily: 'var(--font-body)',
                  fontSize: 'var(--text-base)',
                  fontWeight: 400
                }}
              >
                Explore All Grapes
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Guides Section */}
      <section style={{ backgroundColor: 'var(--linen)' }}>
        <div className="mx-auto max-w-screen-2xl px-8 lg:px-16">
          <div className="py-24 lg:py-32">
            <div className="mb-16">
              <div className="flex items-center gap-3 mb-6">
                <BookOpen size={24} style={{ color: 'var(--oxblood)' }} />
                <h2 
                  style={{ 
                    fontFamily: 'var(--font-display)',
                    fontSize: 'var(--text-3xl)',
                    fontWeight: 300,
                    lineHeight: 'var(--line-height-tight)',
                    color: 'var(--charcoal)',
                    letterSpacing: '-0.02em'
                  }}
                >
                  In-Depth Guides
                </h2>
              </div>
              <p 
                style={{ 
                  fontFamily: 'var(--font-body)',
                  fontSize: 'var(--text-base)',
                  lineHeight: 'var(--line-height-body)',
                  color: 'var(--slate)',
                  fontWeight: 300,
                  maxWidth: '640px'
                }}
              >
                Expert articles and comprehensive guides covering everything from terroir to collecting.
              </p>
            </div>

            <div className="grid gap-6 md:grid-cols-2">
              {guidesData.map((guide) => (
                <div 
                  key={guide.title}
                  className="p-10 cursor-pointer group transition-all duration-300 hover:shadow-lg"
                  style={{ backgroundColor: 'var(--paper)' }}
                >
                  <div className="flex justify-between items-start mb-4">
                    <h3 
                      className="flex-1"
                      style={{ 
                        fontFamily: 'var(--font-display)',
                        fontSize: 'var(--text-xl)',
                        fontWeight: 300,
                        lineHeight: 1.3,
                        color: 'var(--charcoal)'
                      }}
                    >
                      {guide.title}
                    </h3>
                    <span 
                      className="ml-4"
                      style={{ 
                        fontFamily: 'var(--font-body)',
                        fontSize: 'var(--text-xs)',
                        color: 'var(--slate)',
                        fontWeight: 400,
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {guide.readTime}
                    </span>
                  </div>
                  <p 
                    className="mb-6"
                    style={{ 
                      fontFamily: 'var(--font-body)',
                      fontSize: 'var(--text-sm)',
                      lineHeight: 'var(--line-height-body)',
                      color: 'var(--slate)',
                      fontWeight: 300
                    }}
                  >
                    {guide.description}
                  </p>
                  <span 
                    className="inline-block transition-transform duration-200 group-hover:translate-x-1"
                    style={{ 
                      fontFamily: 'var(--font-body)',
                      fontSize: 'var(--text-sm)',
                      color: 'var(--charcoal)',
                      fontWeight: 400
                    }}
                  >
                    Read article →
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
